import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const location = useLocation();
  const navigate = useNavigate();

  // Check localStorage on component mount to see if banner was previously dismissed
  useEffect(() => {
    const bannerDismissed = localStorage.getItem('newPlatformBannerDismissed');
    if (bannerDismissed) {
      setShowBanner(false);
    }
  }, []);

  // Check billing interval preference
  useEffect(() => {
    const checkBillingInterval = () => {
      const consent = localStorage.getItem('cookieConsent');
      if (consent === 'accepted') {
        // Check if there's a billing preference stored
        const billingPref = localStorage.getItem('billingInterval');
        if (billingPref === 'legacy') {
          setBillingInterval('legacy');
        } else if (billingPref === 'yearly') {
          setBillingInterval('yearly');
        } else {
          setBillingInterval('monthly');
        }
      }
    };

    checkBillingInterval();
    
    // Listen for changes to billing preference
    const handleStorageChange = () => {
      checkBillingInterval();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('billingIntervalChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('billingIntervalChanged', handleStorageChange);
    };
  }, []);

  // Countdown to fixed December 31, 2025 23:59:59 (local time)
  useEffect(() => {
    const target = new Date(2025, 11, 31, 23, 59, 59);

    const updateTimeLeft = () => {
      const diffMs = target.getTime() - Date.now();
      if (diffMs <= 0) {
        setTimeLeft('00d 00h 00m 00s');
        setShowBanner(false);
        return;
      }
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
      const seconds = Math.floor((diffMs / 1000) % 60);
      const pad = (n) => String(n).padStart(2, '0');
      setTimeLeft(`${pad(days)}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`);
    };

    updateTimeLeft();
    const intervalId = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const dismissBanner = () => {
    setShowBanner(false);
    // Save dismissal in localStorage so it stays dismissed on page refresh
    localStorage.setItem('newPlatformBannerDismissed', 'true');
  };

  const scrollToSection = (sectionId) => {
    setIsMenuOpen(false);
    
    // Remove the # from the section ID
    const id = sectionId.replace('#', '');
    
    // Check if we're already on the home page
    if (location.pathname === '/') {
      // Find the element by ID
      const element = document.getElementById(id);
      
      // If element exists, scroll to it
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with the hash
      navigate(`/${sectionId}`);
    }
  };

  const normalizedPath = (location.pathname || '').replace(/\/+$/, '') || '/';
  const isTextMessagingPage = normalizedPath === '/text-messaging';
  const signupHref = `https://app.getgolfmarshal.com/signuppro?interval=${billingInterval}`;
  const signupCtaLabel =
    billingInterval === 'legacy'
      ? 'Lock In Legacy'
      : isTextMessagingPage
        ? 'Start Free Trial'
        : 'Get Started';

  const scrollToTop = () => {
    if (location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
    }
  };

  return (
    <>
      {showBanner && !isTextMessagingPage && (
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 shadow-md">
          <div className="container mx-auto px-4 relative flex items-center justify-center">
            <div
              className="flex items-center justify-center mx-auto text-center gap-2 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => scrollToSection('#pricing')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  scrollToSection('#pricing');
                }
              }}
              aria-label="View pricing"
              title="View pricing"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 opacity-90">
                <path fillRule="evenodd" d="M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5zM10.875 6a.75.75 0 011.5 0v5.25h3a.75.75 0 010 1.5h-3.75A.75.75 0 0111.25 12V6z" clipRule="evenodd" />
              </svg>
              <p className="text-sm md:text-base font-semibold drop-shadow">
                Legacy Pricing Available Until 12/31 —
                <span className="ml-2 inline-block bg-white text-primary-700 font-mono font-bold px-2 py-0.5 rounded" aria-live="polite">{timeLeft}</span>!
              </p>
            </div>
            <button 
              onClick={dismissBanner}
              className="absolute right-4 text-white hover:text-gray-200"
              aria-label="Dismiss notification"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
      {!isTextMessagingPage && (
        <div className="bg-gradient-to-r from-secondary-600 to-secondary-700 text-white py-3 shadow-md">
          <div className="container mx-auto px-4 flex items-center justify-center">
            <p className="text-sm md:text-base font-semibold drop-shadow text-center mx-auto max-w-4xl">
              Register your course for text messaging in just 5 minutes —{' '}
              <Link
                to="/text-messaging"
                className="underline underline-offset-2 font-bold hover:opacity-95 whitespace-nowrap"
                aria-label="Learn more about registering your course for text messaging"
              >
                Learn more
              </Link>
            </p>
          </div>
        </div>
      )}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="container py-4 mx-auto flex items-center justify-between">
          <div onClick={scrollToTop} className="flex items-center cursor-pointer">
            <img src="/images/golflogo.jpg" alt="Golf Marshal Logo" className="h-14 mr-2" />
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center justify-center flex-1 mx-4">
            {/* Navigation links removed */}
          </nav>
          
          <div className="hidden md:flex items-center space-x-4">
            <a
              href={signupHref}
              className={isTextMessagingPage ? 'btn btn-secondary' : 'btn btn-primary'}
            >
              {signupCtaLabel}
            </a>
            {!isTextMessagingPage && (
              <div className="relative group">
                <button type="button" className="btn btn-secondary">Sign In</button>
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 invisible group-hover:visible transform opacity-0 group-hover:opacity-100 transition-all duration-300 z-50">
                  <a 
                    href="https://app.getgolfmarshal.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <span className="bg-primary-500 text-white text-xs px-2 py-1 rounded mr-2">NEW</span>
                    <span>Sign In to New Platform</span>
                  </a>
                  <a 
                    href="https://www.getgolfmarshal.com/signin" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded mr-2">CLASSIC</span>
                    <span>Sign In to Classic Site</span>
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Mobile navigation */}
          {isMenuOpen && (
            <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-md py-4">
              <div className="container flex flex-col space-y-4">
                {/* Navigation links removed */}
                <div className="flex flex-col space-y-3 px-4">
                  <a
                    href={signupHref}
                    className={`${isTextMessagingPage ? 'btn btn-secondary' : 'btn btn-primary'} w-full text-center`}
                  >
                    {signupCtaLabel}
                  </a>
                  {!isTextMessagingPage && (
                    <>
                      <a href="https://app.getgolfmarshal.com/" className="btn btn-secondary w-full text-center flex items-center justify-center" target="_blank" rel="noopener noreferrer">
                        <span className="bg-primary-500 text-white text-xs px-2 py-1 rounded mr-2">NEW</span>
                        Sign In to New Platform
                      </a>
                      <a href="https://www.getgolfmarshal.com/signin" className="btn btn-outline w-full text-center flex items-center justify-center" target="_blank" rel="noopener noreferrer">
                        <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded mr-2">CLASSIC</span>
                        Sign In to Classic Site
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header; 